import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/core/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        disabled:
          "border-transparent bg-secondary text-neutral-400 hover:bg-secondary/80",
        quickFilters:
          "border-transparent flex gap-2 justify-between items-center px-3 py-1 h-6 bg-gray-200 rounded-lg hover:bg-gray-800 hover:text-primary-foreground cursor-pointer font-normal hover:font-medium",
        packageUsage:
          "bg-[#FDFCEC] border-[#FEF7C2] text-[#705900] font-normal",
        warningMatte:
          "bg-[#FFF6F0] border-[#F4DCCC] [&_svg]:text-[#783000] text-[#783000] rounded-lg font-medium h-7 px-2",
        yellowMatte:
          "bg-[#FDFCEC] border-[#FEF7C2] [&_svg]:text-[#705900] text-[#705900] rounded-lg font-medium h-7 px-2",
        successMatte:
          "bg-[#EEFCEF] border-[#D1EFE0] [&_svg]:text-[#013D1D] text-[#013D1D] rounded-lg font-medium h-7 px-2",
        infoMatte:
          "bg-[#E8F0FB] border-[#CDD6EC] [&_svg]:text-[#07329F] text-[#041E5F] rounded-lg font-medium h-7 px-2",
        dangerMatte:
          "bg-[#FFEBEA] border-[#F3DADA] [&_svg]:text-[#762929] text-[#762929] rounded-lg font-medium h-7 px-2",
        neutralMatte:
          "bg-[#F4F4F5] border-[#D4D4D8] [&_svg]:text-[#71717A] text-[#71717A] rounded-lg font-medium h-7 px-2",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  tooltip?: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
}

function Badge({
  className,
  variant,
  tooltip,
  tooltipPosition = "top",
  ...props
}: BadgeProps) {
  return (
    <>
      {tooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(badgeVariants({ variant }), className)}
                {...props}
              />
            </TooltipTrigger>
            <TooltipContent
              className="bg-white border border-neutral-200 flex flex-col gap-1 text-muted-foreground text-xs max-w-[300px]"
              side={tooltipPosition}
            >
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className={cn(badgeVariants({ variant }), className)} {...props} />
      )}
    </>
  );
}

export { Badge, badgeVariants };
